import { lazy, Suspense, useEffect } from 'react';

/// components
import Index from "./jsx";
import { connect, useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

// action
import { checkAutoLogin } from './services/AuthService';
import { isAuthenticated } from './store/selectors/AuthSelectors';

/// style
import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";
import ResetPassword from './jsx/pages/ResetPassword';
import PublicBookingPage from './jsx/pages/public/booking/PublicBookingPage';
import PublicEmployeeDashboardPage from './jsx/pages/public/portal/PublicEmployeeDashboardPage';
import CollaboratorPortalLoginPage from './jsx/pages/public/portal/CollaboratorPortalLoginPage';
import PublicMyAppointmentsPage from './jsx/pages/public/booking/PublicMyAppointmentsPage';
import ClubPage from './jsx/pages/public/club/ClubPage';
import PublicPlansPage from './jsx/pages/public/booking/PublicPlansPage';
import ClubPartnerPortalPage from './jsx/pages/public/club/ClubPartnerPortalPage';
import PublicOrderPage from './jsx/pages/public/order/PublicOrderPage';
import PublicOrderTrackPage from './jsx/pages/public/order/PublicOrderTrackPage';
import PublicPage from './jsx/pages/public/page/PublicPage';
import AppointmentConfirmationPage from './jsx/pages/public/booking/AppointmentConfirmationPage';
import AppointmentReviewPage from './jsx/pages/public/review/AppointmentReviewPage';
import SaaSSignupPage from './jsx/pages/public/signup/SaaSSignupPage';
import SaaSBlocked from './jsx/pages/SaaSBlocked';
import ChatwootWidget from './jsx/components/ChatwootWidget';

// Compatibilidade: links antigos /employee/{companyUuid}/{employeeUuid} redirecionam para /p/{slug}/portal.
// O backend resolve o companyUuid como identificador, então usá-lo no lugar do slug funciona.
function LegacyPortalRedirect({ toLogin = false }) {
  const { companyUuid } = useParams();
  return <Navigate to={`/p/${companyUuid}/portal${toLogin ? '/login' : ''}`} replace />;
}

// Links antigos do Clube continuam levando à aba de parceiros do agendamento.
function LegacyClubRedirect() {
  const { companyUuid } = useParams();
  return <Navigate to={`/booking/${companyUuid}/partners`} replace />;
}

const SignUp = lazy(() => import('./jsx/pages/Registration'));
const ForgotPassword = lazy(() => import('./jsx/pages/ForgotPassword'));
const Login = lazy(() => {
  return new Promise(resolve => {
    setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
  });
});

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();

    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }

  return ComponentWithRouterProp;
}

function App(props) {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();  // Obtém a localização atual

  useEffect(() => {
    // Defina as rotas que você quer que não passem pela checagem
    // Adicione outras rotas conforme necessário.
    const routesWithoutCheck = [
      '/forgot-password', '/reset-password',
      // Contratação pelo site: o visitante ainda não tem conta.
      '/signup',

    ];

    // Verifica se a rota atual começa com /booking (rotas públicas de agendamento)
    const isBookingRoute = location.pathname.startsWith('/booking');

    // Verifica se a rota atual começa com /employee (portal do colaborador)
    const isCollaboradorRoute = location.pathname.startsWith('/employee');

    // Verifica se a rota atual começa com /order (rotas públicas de pedido)
    const isOrderRoute = location.pathname.startsWith('/order');

    // Páginas /p/{slug} são públicas e precisam carregar antes do login.
    const isPublicPageRoute = location.pathname.startsWith('/p/');
    // Clube de parceiros: a vitrine do assinante (/club) e a validação do parceiro (/club-partner).
    const isClubRoute = location.pathname.startsWith('/club');
    const isAppointmentConfirmationRoute = location.pathname.startsWith('/appointment-confirmation/');

    // Avaliação pós-atendimento: o cliente chega pelo link do e-mail, sem conta no sistema.
    const isAppointmentReviewRoute = location.pathname.startsWith('/appointment-review/');

    // Verifica se a rota atual não está na lista das rotas que não devem passar pela checagem
    if (!routesWithoutCheck.includes(location.pathname) && !isBookingRoute && !isCollaboradorRoute && !isOrderRoute && !isPublicPageRoute && !isClubRoute && !isAppointmentConfirmationRoute && !isAppointmentReviewRoute) {
      checkAutoLogin(dispatch, navigate);
    }
  }, []);

  let basicRoutes = (
    <Routes>
      <Route path='/login' element={<Login />} />
      <Route path='/page-register' element={<SignUp />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/reset-password' element={<ResetPassword />} />
      <Route path='/saas-blocked' element={<SaaSBlocked />} />
      <Route path='/signup' element={<SaaSSignupPage />} />

      {/** Public */}
      {/** Página pública customizada de introdução */}
      <Route path="/p/:slug" element={<PublicPage />} />
      
      {/** Public Booking */}
      <Route path="/booking/:companyUuid" element={<PublicBookingPage />} />
      {/** Etapas e tela final (/confirmation e o alias /confirmed) passam todas pelo :step.
           Uma rota literal para elas deixa o useParams sem "step", a página não reconhece a
           confirmação e volta para a etapa guardada no rascunho. */}
      <Route path="/booking/:companyUuid/:step" element={<PublicBookingPage />} />
      <Route path="/booking/:companyUuid/my-appointments" element={<PublicMyAppointmentsPage />} />
      <Route path="/booking/:companyUuid/plans" element={<PublicPlansPage />} />
      <Route path="/booking/:companyUuid/partners" element={<ClubPage />} />
      <Route path="/appointment-confirmation/:token" element={<AppointmentConfirmationPage />} />

      {/** Clube de parceiros */}
      <Route path="/club/:companyUuid" element={<LegacyClubRedirect />} />
      {/* Links antigos continuam válidos; o UUID permite revogar o acesso individualmente. */}
      <Route path="/club-partner/:accessUuid" element={<ClubPartnerPortalPage />} />
      <Route path="/p/:slug/club-partner/:accessUuid" element={<ClubPartnerPortalPage />} />
      <Route path="/appointment-review/:token" element={<AppointmentReviewPage />} />

      {/** Portal do Colaborador */}
      <Route path="/p/:slug/portal/login" element={<CollaboratorPortalLoginPage />} />
      <Route path="/p/:slug/portal" element={<PublicEmployeeDashboardPage />} />
      {/** Compatibilidade com links antigos */}
      <Route path="/employee/:companyUuid/:employeeUuid/login" element={<LegacyPortalRedirect toLogin />} />
      <Route path="/employee/:companyUuid/:employeeUuid" element={<LegacyPortalRedirect />} />

      {/** Public Order */}
      <Route path="/order/:companyUuid" element={<PublicOrderPage />} />
      <Route path="/order/:companyUuid/track" element={<PublicOrderTrackPage />} />
    </Routes>
  );

  if (props.isAuthenticated) {
    return (
      <>
        {location.pathname !== '/appointment/agenda' && <ChatwootWidget />}
        <Suspense fallback={
          <div id="preloader">
            <div className="sk-three-bounce">
              <div className="sk-child sk-bounce1"></div>
              <div className="sk-child sk-bounce2"></div>
              <div className="sk-child sk-bounce3"></div>
            </div>
          </div>
        }
        >
          <Index />
        </Suspense>
      </>
    );
  } else {
    return (
      <div className="vh-100">
        <Suspense fallback={
          <div id="preloader">
            <div className="sk-three-bounce">
              <div className="sk-child sk-bounce1"></div>
              <div className="sk-child sk-bounce2"></div>
              <div className="sk-child sk-bounce3"></div>
            </div>
          </div>
        }
        >
          {basicRoutes}
        </Suspense>
      </div>
    );
  }
};

// Informa se o usuário está autenticado. Usa a estrutura do AuthSelectors.
const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
  };
};

// export default connect((mapStateToProps)(App)); 
export default withRouter(connect(mapStateToProps)(App));
